
export const GeradorService = {
  getAll: () => {
    const geradores = JSON.parse(localStorage.getItem('geradores') || '[]');
    return geradores;
  },
  getById: (id: string) => {
    const geradores = JSON.parse(localStorage.getItem('geradores') || '[]');
    return geradores.find((gerador: { id: string; }) => gerador.id === id);
  },
  getByUserId: (userId: string) => {
    const geradores = JSON.parse(localStorage.getItem('geradores') || '[]');
    return geradores.filter((gerador: { criadoPor: string; }) => gerador.criadoPor === userId);
  },
  create: (geradorData: any) => {
    const geradores = JSON.parse(localStorage.getItem('geradores') || '[]');
    const novoGerador = {
      id: geradorData.id || crypto.randomUUID(),
      ...geradorData,
      creditosDisponiveis: geradorData.creditosDisponiveis || geradorData.geracaoEstimada || 0
    };
    geradores.push(novoGerador);
    localStorage.setItem('geradores', JSON.stringify(geradores));
    return novoGerador;
  },
  update: (id: string, geradorData: any) => {
    const geradores = JSON.parse(localStorage.getItem('geradores') || '[]');
    const index = geradores.findIndex((gerador: { id: string; }) => gerador.id === id);
    if (index !== -1) {
      // Preserve the id and any fields not provided in the update
      const geradorAtualizado = {
        ...geradores[index],
        ...geradorData,
        id // Ensure id is always preserved
      };
      
      // Make sure creditosDisponiveis is updated if geracaoEstimada changes
      if (geradorData.geracaoEstimada !== undefined && 
          geradorAtualizado.creditosDisponiveis === undefined) {
        geradorAtualizado.creditosDisponiveis = geradorData.geracaoEstimada;
      }
      
      geradores[index] = geradorAtualizado;
      localStorage.setItem('geradores', JSON.stringify(geradores));
      return geradorAtualizado;
    }
    return null;
  },
  delete: (id: string) => {
    const geradores = JSON.parse(localStorage.getItem('geradores') || '[]');
    const filteredGeradores = geradores.filter((gerador: { id: string; }) => gerador.id !== id);
    localStorage.setItem('geradores', JSON.stringify(filteredGeradores));
    return filteredGeradores.length < geradores.length;
  },
  clearAll: () => {
    localStorage.removeItem('geradores');
    return true;
  }
};

export const ClienteService = {
  getAll: () => {
    const clientes = JSON.parse(localStorage.getItem('clientes') || '[]');
    return clientes;
  },
  getById: (id: string) => {
    const clientes = JSON.parse(localStorage.getItem('clientes') || '[]');
    return clientes.find(cliente => cliente.id === id);
  },
  getByUserId: (userId: string) => {
    const clientes = JSON.parse(localStorage.getItem('clientes') || '[]');
    return clientes.filter(cliente => cliente.criadoPor === userId);
  },
  getByGeradorId: (geradorId: string) => {
    const clientes = JSON.parse(localStorage.getItem('clientes') || '[]');
    return clientes.filter(cliente => cliente.geradorId === geradorId);
  },
  getByTipo: (tipo: 'energia' | 'solar') => {
    const clientes = JSON.parse(localStorage.getItem('clientes') || '[]');
    return clientes.filter(cliente => cliente.tipo === tipo);
  },
  create: (clienteData: any) => {
    const clientes = JSON.parse(localStorage.getItem('clientes') || '[]');
    
    // Set default tipo to 'energia' if not specified
    if (!clienteData.tipo) {
      clienteData.tipo = 'energia';
    }
    
    // Calculate derived fields for energy clients
    let consumoMedio = 0;
    let economiaEstimada = 0;
    let percentualEconomia = 0;
    let gastoAnual = 0;
    
    if (clienteData.tipo === 'energia') {
      consumoMedio = clienteData.gastoEnergia && clienteData.tarifaEnergia 
        ? clienteData.gastoEnergia / clienteData.tarifaEnergia 
        : 0;
      
      gastoAnual = clienteData.gastoEnergia 
        ? clienteData.gastoEnergia * 12 
        : 0;
      
      // If connected to a generator, calculate economics
      if (clienteData.geradorId) {
        const geradores = JSON.parse(localStorage.getItem('geradores') || '[]');
        const gerador = geradores.find((g: any) => g.id === clienteData.geradorId);
        
        if (gerador && clienteData.tarifaEnergia && gerador.tarifaVenda) {
          economiaEstimada = consumoMedio * (clienteData.tarifaEnergia - gerador.tarifaVenda);
          percentualEconomia = clienteData.gastoEnergia > 0 
            ? (economiaEstimada / clienteData.gastoEnergia) * 100 
            : 0;
        }
      }
    }
    
    const novoCliente = {
      id: clienteData.id || crypto.randomUUID(),
      ...clienteData,
      consumoMedio,
      gastoAnual,
      economiaEstimada,
      percentualEconomia,
      status: clienteData.status || "Ativo",
      email: clienteData.email || "",
      celular: clienteData.celular || ""
    };
    
    clientes.push(novoCliente);
    localStorage.setItem('clientes', JSON.stringify(clientes));
    
    // If energy client connected to a gerador, update its available credits
    if (clienteData.tipo === 'energia' && clienteData.geradorId && consumoMedio > 0) {
      const geradores = JSON.parse(localStorage.getItem('geradores') || '[]');
      const index = geradores.findIndex((g: any) => g.id === clienteData.geradorId);
      
      if (index !== -1) {
        const gerador = geradores[index];
        if (gerador.creditosDisponiveis === undefined) {
          gerador.creditosDisponiveis = gerador.geracaoEstimada || 0;
        }
        
        // Subtract the client's consumption from available credits
        gerador.creditosDisponiveis -= consumoMedio;
        geradores[index] = gerador;
        
        localStorage.setItem('geradores', JSON.stringify(geradores));
      }
    }
    
    return novoCliente;
  },
  update: (id: string, clienteData: any) => {
    const clientes = JSON.parse(localStorage.getItem('clientes') || '[]');
    const index = clientes.findIndex(cliente => cliente.id === id);
    
    if (index === -1) return null;
    
    const clienteAtual = clientes[index];
    const consumoMedioAntigo = clienteAtual.consumoMedio || 0;
    
    // Recalculate derived fields for energy clients
    let consumoMedio = clienteAtual.consumoMedio || 0;
    let gastoAnual = clienteAtual.gastoAnual || 0;
    let economiaEstimada = clienteAtual.economiaEstimada || 0;
    let percentualEconomia = clienteAtual.percentualEconomia || 0;
    
    if (clienteAtual.tipo === 'energia') {
      consumoMedio = clienteData.gastoEnergia !== undefined && clienteData.tarifaEnergia !== undefined
        ? clienteData.gastoEnergia / clienteData.tarifaEnergia
        : clienteAtual.consumoMedio || 0;
      
      gastoAnual = clienteData.gastoEnergia !== undefined
        ? clienteData.gastoEnergia * 12
        : clienteAtual.gastoAnual || 0;
      
      // If connected to a generator, recalculate economics
      if (clienteAtual.geradorId) {
        const geradores = JSON.parse(localStorage.getItem('geradores') || '[]');
        const gerador = geradores.find((g: any) => g.id === clienteAtual.geradorId);
        
        if (gerador) {
          const tarifaEnergia = clienteData.tarifaEnergia !== undefined 
            ? clienteData.tarifaEnergia 
            : clienteAtual.tarifaEnergia;
          
          economiaEstimada = consumoMedio * (tarifaEnergia - gerador.tarifaVenda);
          
          const gastoEnergia = clienteData.gastoEnergia !== undefined
            ? clienteData.gastoEnergia
            : clienteAtual.gastoEnergia;
          
          percentualEconomia = gastoEnergia > 0
            ? (economiaEstimada / gastoEnergia) * 100
            : 0;
          
          // Update gerador's available credits
          const geradorIndex = geradores.findIndex((g: any) => g.id === clienteAtual.geradorId);
          if (geradorIndex !== -1) {
            // Add back the old consumption
            geradores[geradorIndex].creditosDisponiveis += consumoMedioAntigo;
            // Subtract the new consumption
            geradores[geradorIndex].creditosDisponiveis -= consumoMedio;
            localStorage.setItem('geradores', JSON.stringify(geradores));
          }
        }
      }
    }
    
    const clienteAtualizado = {
      ...clienteAtual,
      ...clienteData,
      id, // Ensure id is preserved
    };
    
    // Only set calculated fields for energy clients
    if (clienteAtual.tipo === 'energia') {
      clienteAtualizado.consumoMedio = consumoMedio;
      clienteAtualizado.gastoAnual = gastoAnual;
      clienteAtualizado.economiaEstimada = economiaEstimada;
      clienteAtualizado.percentualEconomia = percentualEconomia;
    }
    
    clientes[index] = clienteAtualizado;
    localStorage.setItem('clientes', JSON.stringify(clientes));
    
    return clienteAtualizado;
  },
  delete: (id: string) => {
    const clientes = JSON.parse(localStorage.getItem('clientes') || '[]');
    const clienteToDelete = clientes.find(cliente => cliente.id === id);
    
    if (!clienteToDelete) return false;
    
    // If energy client connected to a gerador, update its available credits
    if (clienteToDelete.tipo === 'energia' && clienteToDelete.geradorId && clienteToDelete.consumoMedio) {
      const geradores = JSON.parse(localStorage.getItem('geradores') || '[]');
      const index = geradores.findIndex((g: any) => g.id === clienteToDelete.geradorId);
      
      if (index !== -1) {
        // Add back the consumption to available credits
        geradores[index].creditosDisponiveis += clienteToDelete.consumoMedio;
        localStorage.setItem('geradores', JSON.stringify(geradores));
      }
    }
    
    const filteredClientes = clientes.filter(cliente => cliente.id !== id);
    localStorage.setItem('clientes', JSON.stringify(filteredClientes));
    
    return true;
  },
  clearAll: () => {
    localStorage.removeItem('clientes');
    return true;
  }
};
